import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { Palette } from '@/themes/themeOverrides/Palette.js';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import React, { useState } from 'react';
import { Loading, Show, SimpleShowLayout, useTranslate } from 'react-admin';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection.jsx';
import { AccessKeySettings, useEditingStates } from '@/components/accessKeys/forms/AccessKeySettings.jsx';
import { useQuickEditField } from '@/composables/useQuickEditField.js';
import { AccessKeyHistory } from '@/components/accessKeys/AccessKeyHistory.jsx';

const StyledShow = styled(Box)(() => ({
    '& .RaEdit-root': {
        width: '100%',
        '& .RaSimpleForm-root': {
            padding: '0 !important',
            width: '100%',
        },
    },
    '& .popup-box--header': {
        cursor: 'pointer',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '20px 20px',
        },
        alignItems: 'center',
        '& h4': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                fontSize: '22px',
            },
        },
        '& .MuiIconButton-root': {
            padding: '12px !important',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                padding: '8px !important',
            },
        },
    },
    '& .MuiStack-root': {
        margin: 0,
    },
    '& .details-simple-show-layout': {
        margin: '0',
        width: '100%',
        '& .select-full-width': {
            width: '100%',
        },
        '& .MuiFormControl-root.select-with-icon': {
            marginBottom: '4px',
            maxWidth: '310px',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                maxWidth: '100%',
            },
        },
        '& .MuiStack-root.ra-field.select-with-icon': {
            borderTop: 'none',
            padding: '6px 0',
            '& .MuiFormHelperText-root': {
                display: 'none',
            },
            '& .MuiTypography-root.MuiTypography-body1.RaLabeled-label': {
                display: 'flex',
                alignItems: 'center',
                marginTop: '11px !important',
            },
        },
        '& .MuiStack-root.ra-field.select-with-icon--border': {
            borderTop: `2px solid ${Palette.gray.gray75}`,
            paddingTop: '32px',
        },
        '& .MuiStack-root.ra-field.select-with-icon-padding': {
            paddingBottom: '32px',
        },
        '& .MuiStack-root.ra-field': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            borderTop: `2px solid ${Palette.gray.gray75}`,
            marginBottom: '0',
            padding: '16px 0',
            '&.field-container': {
                '& .field-container': {
                    width: '100%',
                },
            },
            width: '100%',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                padding: '20px 0',
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
            '&.ra-field-serialNumber': {
                '& span.MuiTypography-body2': {
                    fontSize: '18px',
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontWeight: '700',
                },
            },
            '& span.MuiTypography-body2': {
                fontSize: '14px',
            },
        },
        '& .MuiStack-root.ra-field:first-of-type': {
            borderTop: `0`,
        },
    },
    '& .MuiTypography-root.RaLabeled-label': {
        fontSize: '14px !important',
        fontWeight: '400',
        lineHeight: '24px',
        width: '160px',
        minWidth: '160px',
        color: Palette.gray.gray700,
        margin: '0 !important',
        paddingRight: '16px',
    },
    '& .MuiButtonBase-root.close-popup': {
        padding: '12px 16px !important',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '12px !important',
        },
    },
    '& .MuiButtonBase-root': {
        '& .MuiCircularProgress-root': {
            display: 'none',
        },
    },
    '& .MuiTypography-h5': {
        fontSize: '22px',
    },
    '& .present-state': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiCard-root.scroll-container': {
        paddingBottom: '4px',
    },
    '& .MuiToolbar-root.MuiToolbar-gutters.MuiTablePagination-toolbar': {
        borderBottom: `2px solid ${Palette.gray.gray75}`,
        padding: '9px 0',
    },
    '& .chip-small': {
        height: '20px',
        borderRadius: '6px',
        '& .MuiChip-label': {
            padding: '0 6px',
        },
    },
}));
export const AccessKeyShow = ({ elementId }) => {
    const translate = useTranslate();
    const resource = 'access-key';
    const editingStates = useEditingStates();
    const { save, isLoadingEdit } = useQuickEditField(resource, elementId, editingStates);

    const [isSettingsOpen, setIsSettingsOpen] = useState(true);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    if (isLoadingEdit) {
        return <Loading />;
    }

    return (
        <StyledShow>
            <Show id={elementId} actions={false} resource={resource}>
                <SimpleShowLayout className='simple-show-layout'>
                    <CollapsibleSection
                        title={translate('resources.accessKey.labels.settings')}
                        isOpen={isSettingsOpen}
                        onToggle={() => setIsSettingsOpen(!isSettingsOpen)}
                    >
                        <AccessKeySettings save={save} editingStates={editingStates} />
                    </CollapsibleSection>
                    <CollapsibleSection
                        title={translate('resources.accessKey.labels.history')}
                        isOpen={isHistoryOpen}
                        onToggle={() => setIsHistoryOpen(!isHistoryOpen)}
                    >
                        <AccessKeyHistory accessKeyId={elementId} />
                    </CollapsibleSection>
                </SimpleShowLayout>
            </Show>
        </StyledShow>
    );
};
